import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';
import { subscribeTable } from '@/lib/realtime';
import { findCity, type City } from './cities';

export interface ActiveTravel {
  city: City;
  until: string;
}

export interface TravelState {
  travel: ActiveTravel | null;
  loading: boolean;
  setTravel(cityKey: string, durationDays: number): Promise<{ ok: boolean; error?: string }>;
  clearTravel(): Promise<{ ok: boolean; error?: string }>;
}

/**
 * Travel mode — premium-gated city override for geolocation.
 *
 * Read: poll once + subscribe to changes on the user's row. Auto-clear
 * when the until-timestamp has passed (the row may linger on the server
 * until the cleanup cron sweeps it, so we filter client-side too).
 */
export const useTravelMode = (): TravelState => {
  const { user } = useAuth();
  const [travel, setTravelState] = useState<ActiveTravel | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setTravelState(null);
      setLoading(false);
      return;
    }
    const supabase = getSupabase();
    if (!supabase) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase
        .from('hottr_travel_overrides')
        .select('city_key, until')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      const row = data as { city_key: string; until: string } | null;
      if (!row || new Date(row.until).getTime() < Date.now()) {
        setTravelState(null);
        setLoading(false);
        return;
      }
      const city = findCity(row.city_key);
      setTravelState(city ? { city, until: row.until } : null);
      setLoading(false);
    };
    load();

    const unsubscribe = subscribeTable(
      supabase,
      `hottr-travel-${user.id}`,
      {
        event: '*',
        table: 'hottr_travel_overrides',
        filter: `user_id=eq.${user.id}`,
      },
      load,
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user?.id]);

  const setTravel = useCallback(
    async (cityKey: string, durationDays: number): Promise<{ ok: boolean; error?: string }> => {
      if (!user) return { ok: false, error: 'Not logged in.' };
      const city = findCity(cityKey);
      if (!city) return { ok: false, error: 'Unknown city.' };
      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'No connection.' };

      const until = new Date(Date.now() + durationDays * 86_400_000).toISOString();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const db = supabase as any;
      const { error } = (await db.from('hottr_travel_overrides').upsert(
        {
          user_id: user.id,
          city_key: city.key,
          lat: city.lat,
          lng: city.lng,
          until,
        },
        { onConflict: 'user_id' },
      )) as { error: { message: string } | null };
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    },
    [user?.id],
  );

  const clearTravel = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (!user) return { ok: false, error: 'Not logged in.' };
    const supabase = getSupabase();
    if (!supabase) return { ok: false, error: 'No connection.' };
    const { error } = await supabase
      .from('hottr_travel_overrides')
      .delete()
      .eq('user_id', user.id);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  }, [user?.id]);

  return { travel, loading, setTravel, clearTravel };
};
